import { v4 as uuid } from '@lukeed/uuid'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { TARGET_TYPES } from 'components/attacks/create/targets/target-utils'
import { getServiceIdFromAttack } from 'components/attacks/utils/attack-configuration'
import { getTargetType } from 'components/scenarios/concurrent/attack-attributes'
import {
  connectAndReorderBranchNodes,
  deleteGraphNodeAtPath,
  findNearestAttackNode,
  getNodeAtPath,
  getNodeParentPath,
  type GraphNodePath,
  type GraphNodePathMap,
  isConcurrentNode,
  isFailureFlagNode,
  isInfraAttackNode,
  replaceGraphNodeAtPath
} from 'components/scenarios/utils/graph'
import {
  type BaseGraphNode,
  type ContinuousStatusCheckNode,
  type FailureFlagInputForScenario,
  type FailureFlagNode,
  type GraphNode,
  type GraphNodeList,
  type ScenarioGraph,
  ScenarioGraphNodeType
} from 'types/api/scenarios/scenario'
import { swapArrayElements } from 'utils/list'

import type { ScenarioFormValues } from 'components/scenarios/create/create-utils'
import type { FormikHelpers } from 'formik'

export type EditorType = typeof EditorTypes[keyof typeof EditorTypes]

export type ActiveNode =
  | (Omit<BaseGraphNode, 'type'> & { type: ScenarioGraphNodeType })
  | null

export interface EditorNodeState {
  activeNode: ActiveNode
  isEditing: boolean
  editorType: EditorType
  nodePath?: GraphNodePath
}

export const EditorTypes = {
  CompletedAttack: 'CompletedAttack',
  ConcurrentNode: 'ConcurrentNode',
  ContinuousStatusCheck: 'ContinuousStatusCheck',
  Delay: 'Delay',
  NewFailureFlagImpact: 'NewFailureFlagImpact',
  ExistingFailureFlag: 'ExistingFailureFlag',
  NewAttack: 'NewAttack',
  None: 'None',
  ScenarioAttack: 'ScenarioAttack', // copy another attack from within this scenario
  StatusCheck: 'StatusCheck'
} as const

export interface NodeActions {
  closeEditor: () => EditorNodeState
  newNode: (type: EditorType, nodePath: GraphNodePath) => EditorNodeState
  selectNode: (node: ActiveNode, nodePath: GraphNodePath) => EditorNodeState
}

export interface GraphEditorContextValue {
  basePathName?: string
  failureFlagInputForScenario?: FailureFlagInputForScenario
  graph: ScenarioGraph
  graphNodePaths: GraphNodePathMap
  handleAddAttackNode: (nodePath: GraphNodePath) => void
  handleAddBranchAfter: (nodePath: GraphNodePath) => void
  handleAddBranchBefore: (nodePath: GraphNodePath) => void
  handleAddCompletedAttack: (nodePath: GraphNodePath) => void
  handleAddConcurrentNode: (nodePath: GraphNodePath) => void
  handleAddDelayNode: (nodePath: GraphNodePath) => void
  handleAddExistingFailureFlagExperiment: (nodePath: GraphNodePath) => void
  handleAddFailureFlagNode: (nodePath: GraphNodePath) => void
  handleAddStatusCheckNode: (nodePath: GraphNodePath) => void
  handleDeleteBranch: (nodePath: GraphNodePath) => void
  handleDeleteConcurrentNode: (nodePath: GraphNodePath) => void
  handleMoveBranchDown: (nodePath: GraphNodePath) => void
  handleMoveBranchUp: (nodePath: GraphNodePath) => void
  handleMoveNodeDown: (nodePath: GraphNodePath) => void
  handleMoveNodeUp: (nodePath: GraphNodePath) => void
  handleEditNode: (
    node: GraphNode | ContinuousStatusCheckNode | FailureFlagNode
  ) => void
  handleRemoveAttack: (node: GraphNode) => void
  handleUpdateNode: (node: GraphNode) => void
  nodeActions?: NodeActions
  nodeState?: EditorNodeState
  setFailureFlagInputForScenario: React.Dispatch<
    React.SetStateAction<FailureFlagInputForScenario | undefined>
  >
}

export const GraphEditorContext = createContext<
  GraphEditorContextValue | undefined
>(undefined)

export const GraphEditorContextProvider: React.FC<{
  basePathName?: string
  graph: ScenarioGraph
  graphNodePaths: GraphNodePathMap
  nodeActions?: NodeActions
  nodeState?: EditorNodeState
  setFieldValue?: FormikHelpers<ScenarioFormValues>['setFieldValue']
}> = ({
  basePathName,
  children,
  graph,
  graphNodePaths,
  nodeActions,
  nodeState,
  setFieldValue
}) => {
  const navigate = useNavigate()
  const { state: locationState } = useLocation()

  const handleAddAttackNode = useCallback(
    (nodePath: GraphNodePath) => {
      const nearestAttackNode = findNearestAttackNode(graph, nodePath)

      if (!nodeActions) {
        return
      }

      if (!nearestAttackNode) {
        nodeActions.selectNode(null, nodePath)
        return
      }

      // clone it so we aren't modifying the other attack node's reference
      const referenceAttackNode = structuredClone(nearestAttackNode)
      referenceAttackNode.guid = uuid()
      // id is the object key for now, which is the last part of the path
      // eventually this will be replaced with serializeNodePathToId()
      referenceAttackNode.id = String(nodePath.at(-1))

      nodeActions.selectNode(referenceAttackNode, nodePath)
    },
    [graph, nodeActions]
  )

  const handleAddBranchBefore = useCallback(
    (nodePath: GraphNodePath) => {
      const emptyBranch: ScenarioGraph = { start_id: '0', nodes: {} }
      const branchListPath = getNodeParentPath(nodePath)
      const parentPath = getNodeParentPath(branchListPath)
      const parent = getNodeAtPath(graph, parentPath) as GraphNode

      // type guard
      if (!isConcurrentNode(parent)) {
        return
      }

      const branchIndex = Number(nodePath.at(-1))
      if (branchIndex === 0) {
        parent.branches.unshift(emptyBranch)
      } else {
        parent.branches.splice(branchIndex, 0, emptyBranch)
      }

      const newGraph = replaceGraphNodeAtPath(graph, parent, parentPath)
      connectAndReorderBranchNodes(newGraph)

      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleAddBranchAfter = useCallback(
    (nodePath: GraphNodePath) => {
      const emptyBranch = { start_id: '0', nodes: {} }
      const branchListPath = getNodeParentPath(nodePath)
      const parentPath = getNodeParentPath(branchListPath)
      const parent = getNodeAtPath(graph, parentPath) as GraphNode

      // type guard
      if (!isConcurrentNode(parent)) {
        return
      }

      const branchIndex = Number(nodePath.at(-1))
      parent.branches.splice(branchIndex + 1, 0, emptyBranch)

      const newGraph = replaceGraphNodeAtPath(graph, parent, parentPath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleAddCompletedAttack = useCallback(
    (nodePath: GraphNodePath) => {
      if (!nodeActions) {
        return
      }

      nodeActions.newNode(EditorTypes.CompletedAttack, nodePath)
    },
    [nodeActions]
  )

  const handleAddConcurrentNode = useCallback(
    (nodePath: GraphNodePath) => {
      // add TWO branches to start with because concurrent nodes should have at least 2 branches
      const initialNodes1: GraphNodeList = {}
      const initialBranch1: ScenarioGraph = {
        start_id: '0',
        nodes: initialNodes1
      }
      const initialNodes2: GraphNodeList = {}
      const initialBranch2: ScenarioGraph = {
        start_id: '0',
        nodes: initialNodes2
      }
      const node: GraphNode = {
        type: ScenarioGraphNodeType.Concurrent,
        guid: uuid(),
        branches: [initialBranch1, initialBranch2],
        id: String(nodePath.at(-1))
      }

      // special case - user is adding a concurrent node to the root level
      // convert the root to a concurrent node and move any existing nodes into the first branch
      // nodePath is only two levels deep for root nodes, e.g., ['nodes', 0]
      const isReplacingRootNode = nodePath.length <= 2
      if (isReplacingRootNode) {
        const existingNodes = graph.nodes
        node.branches[0].nodes = existingNodes
        graph.nodes = {}
        nodePath = ['nodes', '0']
      }

      const newGraph = replaceGraphNodeAtPath(graph, node, nodePath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleAddDelayNode = useCallback(
    (nodePath: GraphNodePath) => {
      const delayNode: GraphNode = {
        type: ScenarioGraphNodeType.Delay,
        guid: uuid(),
        delay: 5,
        id: String(nodePath.at(-1)),
        next: undefined
      }

      const newGraph = replaceGraphNodeAtPath(graph, delayNode, nodePath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleAddExistingFailureFlagExperiment = useCallback(
    (nodePath: GraphNodePath) => {
      nodeActions?.newNode(EditorTypes.ExistingFailureFlag, nodePath)
    },
    [nodeActions]
  )

  const handleAddFailureFlagNode = useCallback(
    (nodePath: GraphNodePath) => {
      if (!nodeActions) {
        return
      }

      nodeActions.selectNode(
        {
          type: ScenarioGraphNodeType.FailureFlag
        },
        nodePath
      )
    },
    [nodeActions]
  )

  const handleAddStatusCheckNode = useCallback(
    (nodePath: GraphNodePath) => {
      if (!nodeActions) {
        return
      }

      const node = {
        type: ScenarioGraphNodeType.SynchronousStatusCheck,
        guid: uuid(),
        id: String(nodePath.at(-1)),
        next: undefined
      }

      nodeActions.selectNode(node, nodePath)
    },
    [nodeActions]
  )

  // FYI used the verb "delete" here instead of "remove" because it's more visually distinct from "move"
  const handleDeleteBranch = useCallback(
    (nodePath: GraphNodePath) => {
      const newGraph = deleteGraphNodeAtPath(graph, nodePath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleDeleteConcurrentNode = useCallback(
    (nodePath: GraphNodePath) => {
      const newGraph = deleteGraphNodeAtPath(graph, nodePath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const [failureFlagInputForScenario, setFailureFlagInputForScenario] =
    useState<FailureFlagInputForScenario | undefined>()

  const handleEditNode = useCallback(
    (node: GraphNode | ContinuousStatusCheckNode | FailureFlagNode) => {
      if (isInfraAttackNode(node) && basePathName) {
        const targetType = getTargetType(node)
        const targetTypeDefinition = TARGET_TYPES[targetType]

        const serviceId = getServiceIdFromAttack(node)

        // Update the url based on the selected nodes target type
        if (serviceId) {
          navigate(`${basePathName}/services`, {
            replace: true,
            state: {
              ...locationState,
              serviceId
            }
          })
        } else {
          navigate(`${basePathName}/${targetTypeDefinition.path}`, {
            replace: true,
            state: locationState
          })
        }
      }

      if (isFailureFlagNode(node) && basePathName) {
        navigate(`${basePathName}/failure-flags`, {
          replace: true,
          state: locationState
        })
        setFailureFlagInputForScenario(node.failureFlagInputForScenario)
      }

      if (!nodeActions) {
        return
      }

      nodeActions.selectNode(node, graphNodePaths[node.guid])
    },
    [nodeActions, graphNodePaths, navigate, basePathName, locationState]
  )

  const handleMoveBranchDown = useCallback(
    (nodePath: GraphNodePath) => {
      const branchListPath = getNodeParentPath(nodePath)
      const parentPath = getNodeParentPath(branchListPath)
      const parent = getNodeAtPath(graph, parentPath) as GraphNode

      // type guard
      if (!isConcurrentNode(parent)) {
        return
      }

      const targetBranchIndex = Number(nodePath.at(-1))
      swapArrayElements(
        parent.branches,
        targetBranchIndex,
        targetBranchIndex + 1
      )

      const newGraph = replaceGraphNodeAtPath(graph, parent, parentPath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleMoveBranchUp = useCallback(
    (nodePath: GraphNodePath) => {
      const branchListPath = getNodeParentPath(nodePath)
      const parentPath = getNodeParentPath(branchListPath)
      const parent = getNodeAtPath(graph, parentPath) as GraphNode

      // type guard
      if (!isConcurrentNode(parent)) {
        return
      }

      const targetBranchIndex = Number(nodePath.at(-1))
      swapArrayElements(
        parent.branches,
        targetBranchIndex,
        targetBranchIndex - 1
      )

      const newGraph = replaceGraphNodeAtPath(graph, parent, parentPath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleMoveNodeDown = useCallback(
    (nodePath: GraphNodePath) => {
      const parentPath = getNodeParentPath(nodePath)
      const parent = getNodeAtPath(graph, parentPath)
      const currentGraphKey = Number(nodePath.at(-1)) // last part is its key for the parent nodes object
      const targetGraphKey = currentGraphKey + 1

      if (!parent) {
        return
      }

      const tempNode = parent[targetGraphKey]

      const newParent: GraphNode = {
        ...parent,
        [targetGraphKey]: {
          ...parent[currentGraphKey],
          id: targetGraphKey
        },
        [currentGraphKey]: {
          ...tempNode,
          id: currentGraphKey
        }
      }

      const newGraph = replaceGraphNodeAtPath(graph, newParent, parentPath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleMoveNodeUp = useCallback(
    (nodePath: GraphNodePath) => {
      const parentPath = getNodeParentPath(nodePath)
      const parent = getNodeAtPath(graph, parentPath)
      const currentGraphKey = Number(nodePath.at(-1)) // last part is its key for the parent nodes object
      const targetGraphKey = currentGraphKey - 1

      if (!parent) {
        return
      }

      const tempNode = parent[targetGraphKey]
      const newParent: GraphNode = {
        ...parent,
        [targetGraphKey]: {
          ...parent[currentGraphKey],
          id: targetGraphKey
        },
        [currentGraphKey]: {
          ...tempNode,
          id: currentGraphKey
        }
      }

      const newGraph = replaceGraphNodeAtPath(graph, newParent, parentPath)
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph]
  )

  const handleRemoveAttack = useCallback(
    (node) => {
      const newGraph = deleteGraphNodeAtPath(graph, graphNodePaths[node.guid])
      // ensure all nodes are connected properly before updating the graph
      // to ensure that it renders properly since it follows the `next` field to render in order
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph, graphNodePaths]
  )

  const handleUpdateNode = useCallback(
    (node) => {
      const newGraph = replaceGraphNodeAtPath(
        graph,
        node,
        graphNodePaths[node.guid]
      )
      connectAndReorderBranchNodes(newGraph)
      setFieldValue?.('graph', newGraph)
    },
    [setFieldValue, graph, graphNodePaths]
  )

  const value = useMemo<GraphEditorContextValue>(
    () => ({
      basePathName,
      failureFlagInputForScenario,
      graph,
      graphNodePaths,
      handleAddAttackNode,
      handleAddBranchAfter,
      handleAddBranchBefore,
      handleAddCompletedAttack,
      handleAddConcurrentNode,
      handleAddDelayNode,
      handleAddExistingFailureFlagExperiment,
      handleAddFailureFlagNode,
      handleAddStatusCheckNode,
      handleDeleteBranch,
      handleDeleteConcurrentNode,
      handleMoveBranchDown,
      handleMoveBranchUp,
      handleMoveNodeDown,
      handleMoveNodeUp,
      handleEditNode,
      handleRemoveAttack,
      handleUpdateNode,
      nodeActions,
      nodeState,
      setFailureFlagInputForScenario
    }),
    [
      basePathName,
      failureFlagInputForScenario,
      graph,
      graphNodePaths,
      handleAddAttackNode,
      handleAddBranchAfter,
      handleAddBranchBefore,
      handleAddCompletedAttack,
      handleAddConcurrentNode,
      handleAddDelayNode,
      handleAddExistingFailureFlagExperiment,
      handleAddFailureFlagNode,
      handleAddStatusCheckNode,
      handleDeleteBranch,
      handleDeleteConcurrentNode,
      handleMoveBranchDown,
      handleMoveBranchUp,
      handleMoveNodeDown,
      handleMoveNodeUp,
      handleEditNode,
      handleRemoveAttack,
      handleUpdateNode,
      nodeActions,
      nodeState,
      setFailureFlagInputForScenario
    ]
  )

  return (
    <GraphEditorContext.Provider value={value}>
      {children}
    </GraphEditorContext.Provider>
  )
}

export const useGraphEditorContext = () => {
  const context = useContext(GraphEditorContext)

  if (!context) {
    throw new Error(
      'useGraphEditorContext must be used within its respective provider'
    )
  }

  return context
}
